import React, { useEffect, useCallback, useState } from 'react';
import useInterval from 'react-useinterval';
import { mintNumberToName } from 'utils/token';
import { usePlayerContext } from './PlayerContext';
import { Box, Flex } from '../ui';
import { getDropFromContractAddress } from 'utils/dropsHelpers';
import useLocalStorage from 'hooks/useLocalStorage';
import { PlayState } from '../Visualization';
import ActiveBeat from './ActiveBeat';
import { toHexSeed } from 'utils/token';
import { logEvent } from 'utils/analytics';
import { useActiveBeat } from 'hooks';
import PlayerViz from './PlayerViz';
import PlayerControls from './PlayerControls';
import Chords from './Chords';
import usePlayback from './usePlayback';
import EulerBeatsWrapper from 'utils/EulerBeatsWrapper';
import { DROP } from 'components/common';

const getVisElement = (drop, id) => {
  const seed = toHexSeed(drop, id);
  const mainElementId = drop === DROP.futura ? id : `eulerbeats-original-${seed}`;
  return document.getElementById(mainElementId);
};

const Player: React.FC<any> = ({ order, isFullScreen, toggleFullScreen }) => {
  const { activeBeat, id, isPrint } = useActiveBeat();
  const { dispatch, autoPlayNext } = usePlayerContext();
  const [isPlaying, setIsPlaying] = useState(autoPlayNext);
  const [chordsVisible, setChordsVisible] = useLocalStorage<any>('eb.chordsVisible', false);
  const drop = activeBeat && getDropFromContractAddress(activeBeat.contractAddress);
  const isFutura = drop === DROP.futura;

  const {
    playbackRate,
    setPlaybackRate,
    currentTimePercentage,
    currentTime,
    duration,
    lastChordIndex,
    lastChordIsPlaying,
    lastChordProgress,
    chords,
    setCurrentTimePercentage,
    setCurrentTime,
    audioRewind,
  } = usePlayback();

  useInterval(() => {
    if (isFutura) {
      setIsPlaying(window.EulerBeatsFutura?.visual?.isPlaying());
    }
  }, 500);

  const playVisual = useCallback(() => {
    if (!activeBeat || !drop) {
      return;
    }
    const element = getVisElement(drop, id);
    if (!element) {
      console.log('no element', id);
      return;
    }
    logEvent('play_beat', {
      event_category: 'EulerBeat Interaction',
      event_label: 'Play Euler Beat',
      name: element ? element.id : '',
    });
    console.log('play eb ' + element.id);
    EulerBeatsWrapper.visual.stop();
    if (drop === DROP.futura) {
      window.EulerBeatsFutura.audio.removeListener('endPiece', playNext);
    }
    EulerBeatsWrapper.visual.play(drop, element, false).catch(console.error);
    if (drop === DROP.futura) {
      window.EulerBeatsFutura.audio.addListener('endPiece', playNext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drop, id, activeBeat]);

  useEffect(() => {
    if (EulerBeatsWrapper.visual.isPlaying() && drop !== DROP.futura) {
      return;
    }
    if (autoPlayNext && activeBeat?.id === id) {
      playVisual();
      audioRewind();
    } else if (activeBeat?.id !== id) {
      audioRewind();
    }
  }, [id, activeBeat, playVisual, autoPlayNext, audioRewind, drop]);

  const onToggle = useCallback(() => {
    if (!activeBeat || !drop) {
      return;
    }
    EulerBeatsWrapper.audio.updateCurrentTimePercentage(currentTimePercentage);

    const element = getVisElement(drop, id);
    logEvent('toggle_beat', {
      event_category: 'EulerBeat Interaction',
      event_label: 'Click on Toggle button',
      name: element ? element.id : '',
    });

    if (drop === DROP.futura) {
      window.EulerBeatsFutura.audio.removeListener('endPiece', playNext);
    }
    EulerBeatsWrapper.visual.toggle(drop, element, false);
    if (drop === DROP.futura) {
      window.EulerBeatsFutura.audio.addListener('endPiece', playNext);
    }

    if (isPlaying) {
      setIsPlaying(false);
      dispatch({ type: 'stop' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeBeat, drop, currentTimePercentage, isPlaying, dispatch]);

  const onPlayVisual = useCallback(
    (event: PlayState) => {
      console.log('play event', event);
      if (event === PlayState.start) {
        setIsPlaying(true);
      } else if (event === PlayState.end) {
        dispatch({
          type: 'next',
          autoPlayNext: true,
        });
      }
    },
    [dispatch]
  );

  const updatePlaybackRate = value => {
    setPlaybackRate(value);
    EulerBeatsWrapper.audio.updatePlaybackRate(value);
  };

  const updateCurrentTimePercentage = useCallback(
    value => {
      setCurrentTimePercentage(value);
      EulerBeatsWrapper.audio.updateCurrentTimePercentage(value);
      const timeInfo = EulerBeatsWrapper.audio.getPlayerState();
      if (timeInfo) {
        setCurrentTime(timeInfo.currentTime);
      }
    },
    [setCurrentTime, setCurrentTimePercentage]
  );

  const onPrevious = useCallback(() => {
    if (currentTimePercentage < 1) {
      dispatch({ type: 'previous', autoPlayNext: EulerBeatsWrapper.visual.isPlaying() });
    } else {
      updateCurrentTimePercentage(0);
    }
  }, [currentTimePercentage, dispatch, updateCurrentTimePercentage]);

  const onNext = useCallback(() => {
    dispatch({ type: 'next', autoPlayNext: EulerBeatsWrapper.visual.isPlaying() });
  }, [dispatch]);

  const playNext = useCallback(() => {
    dispatch({ type: 'next', autoPlayNext: true });
  }, [dispatch]);

  const toggleChords = () => {
    setChordsVisible(!chordsVisible);
  };

  let title = '';
  if (activeBeat && drop) {
    title = `${mintNumberToName(activeBeat.mintNumber, drop)}${isPrint ? ' - PRINT' : ''}`;
  }

  return (
    <Flex width="100%" column justifyCenter alignCenter order={order}>
      <Box width={isFullScreen ? 'auto' : '100%'} position="relative">
        <PlayerViz
          activeBeat={activeBeat}
          onPlay={onPlayVisual}
          playbackRate={playbackRate}
          isFullScreen={isFullScreen}
        />

        {chordsVisible && (
          <Chords
            lastChordIndex={lastChordIndex}
            lastChordIsPlaying={lastChordIsPlaying}
            lastChordProgress={lastChordProgress}
            chords={chords}
          />
        )}

        <PlayerControls
          activeBeat={activeBeat}
          title={title}
          isPlaying={isPlaying}
          onPrevious={onPrevious}
          onToggle={onToggle}
          onNext={onNext}
          playbackRate={playbackRate}
          onUpdatePlaybackRate={updatePlaybackRate}
          currentTimePercentage={currentTimePercentage}
          currentTime={currentTime}
          duration={duration}
          onUpdateCurrentTimePercentage={updateCurrentTimePercentage}
          toggleChords={toggleChords}
          chordsVisible={chordsVisible}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          isFutura={isFutura}
        />
      </Box>

      {!isFullScreen && <ActiveBeat />}
    </Flex>
  );
};

export default Player;
